// Weekly-average bucketing for weight series — the client-side mirror of the server's
// WeightTrackService.getHistoryForRange / createWeeklyDot.
//
// Why this exists: the "סה״כ" total-change badge was computed two different ways. The trainer
// profile (TraineeProfileDialog → WeightChart) subtracts the first weekly AVERAGE from the last,
// using the server-built `weightSeries`. The client card (MetricsChart) subtracted the first RAW
// daily weigh-in from the last. Same underlying daily_metrics rows, two different numbers on screen
// (−1.3 vs −1.0 for the same trainee and the same "חודש" selection), both labelled "סה״כ".
//
// A single noisy morning weigh-in should not move a MONTHLY headline number, so weekly averages win
// and this module lets the daily-series screen compute them without a new endpoint.
//
// The bucketing MUST match the server or the two screens drift again:
//   - week starts on SUNDAY               (WeightTrackService.getFirstDayOfWeek: .with(DayOfWeek.SUNDAY))
//   - a bucket's value is the MEAN of that week's weigh-ins, rounded to 2 decimals
//                                          (WeightTrackService.createWeeklyDot: average + roundToTwoDecimals)
//   - buckets with no usable weight are dropped, and the result is sorted oldest-first

/** A single daily reading: `date` is epoch SECONDS (the wire format used across the admin charts). */
export interface DailyPoint {
  date: number;
  value: number | null | undefined;
}

/** One weekly bucket: `date` is the epoch-seconds timestamp of that week's Sunday. */
export interface WeeklyPoint {
  date: number;
  value: number;
}

const SECONDS_PER_DAY = 86400;

/**
 * Epoch-seconds timestamp of the SUNDAY starting the week that contains `epochSecs`.
 *
 * Uses UTC parts deliberately: the daily points come from the server already normalised to a
 * calendar day, so re-zoning them to the viewer's locale would shift a weigh-in across a week
 * boundary and silently change which bucket it lands in.
 */
export function startOfWeekUtc(epochSecs: number): number {
  const dayStart = Math.floor(epochSecs / SECONDS_PER_DAY) * SECONDS_PER_DAY;
  // Unix epoch (1970-01-01) was a Thursday, so day 0 is index 4 in a Sunday-first week.
  const dayIndex = Math.floor(dayStart / SECONDS_PER_DAY);
  const dayOfWeek = (((dayIndex + 4) % 7) + 7) % 7; // 0 = Sunday
  return dayStart - dayOfWeek * SECONDS_PER_DAY;
}

/** Round to 2 decimals — mirrors the server's roundToTwoDecimals on each weekly dot. */
function roundToTwoDecimals(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Group daily readings into Sunday-anchored weekly averages, oldest first.
 *
 * Points with a null/undefined/non-finite value are ignored, and a week left with no usable
 * reading produces no bucket (rather than a zero) — matching the server, which returns null from
 * createWeeklyDot and filters it out.
 */
export function toWeeklyAverages(points: DailyPoint[] | null | undefined): WeeklyPoint[] {
  if (!Array.isArray(points) || points.length === 0) return [];

  const buckets = new Map<number, number[]>();
  points.forEach((p) => {
    if (p == null || typeof p.date !== "number" || !Number.isFinite(p.date)) return;
    const v = p.value;
    if (v == null || typeof v !== "number" || !Number.isFinite(v)) return;
    const weekStart = startOfWeekUtc(p.date);
    const bucket = buckets.get(weekStart);
    if (bucket) bucket.push(v);
    else buckets.set(weekStart, [v]);
  });

  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([date, values]) => ({
      date,
      value: roundToTwoDecimals(values.reduce((sum, v) => sum + v, 0) / values.length),
    }));
}

/**
 * Total change across a series: last minus first. Returns null when there are fewer than two
 * points, since a single reading describes no change.
 *
 * Deliberately shares the shape of the coach dashboard's `seriesDelta` so both screens answer
 * "how much did this trainee change over the range" identically.
 */
export function seriesTotalDelta(series: WeeklyPoint[] | null | undefined): number | null {
  if (!Array.isArray(series) || series.length < 2) return null;
  const first = series[0]?.value;
  const last = series[series.length - 1]?.value;
  if (first == null || last == null) return null;
  return last - first;
}

/**
 * The badge number for a daily series: bucket into weekly averages, then take last − first.
 * Rounded to 1 decimal, which is the precision the badge renders at.
 */
export function weeklyAverageDelta(points: DailyPoint[] | null | undefined): number | null {
  const delta = seriesTotalDelta(toWeeklyAverages(points));
  return delta == null ? null : +delta.toFixed(1);
}
